import React, { useEffect, useState, useCallback } from "react";
import ThemedBackground from "@/components/ThemedBackground";
import { api } from "@/lib/api";

/**
 * SpendTracker page at /spend — Emergent AI deployment spend.
 * Owner asked for a manual tracker across the apps with a "🔊 Read
 * summary" Web Speech button. Backend already seeded ~$3,651 across the
 * 6 sister apps (see server.py `/api/spend/seed`).
 */

function speak(text) {
  try {
    const synth = window.speechSynthesis;
    if (!synth) return;
    synth.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.0;
    u.pitch = 1.0;
    u.lang = "en-GB";
    const voices = synth.getVoices();
    const pref = voices.find(v => /en-GB/i.test(v.lang) && /male|daniel/i.test(v.name))
              || voices.find(v => /en-GB/i.test(v.lang))
              || voices[0];
    if (pref) u.voice = pref;
    synth.speak(u);
  } catch (_) { /* no-op */ }
}

export default function SpendTracker() {
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // New-entry form
  const [appName, setAppName] = useState("");
  const [domain, setDomain] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [s, e] = await Promise.all([
        api.get("/spend/summary").then(r => r.data),
        api.get("/spend").then(r => r.data),
      ]);
      setSummary(s);
      setEntries(e.entries || []);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2?.message || "Failed to load spend data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addEntry = async () => {
    if (!appName.trim() || !amount) return;
    setSaving(true);
    try {
      await api.post("/spend", {
        app_name: appName.trim(),
        domain: domain.trim(),
        amount_usd: parseFloat(amount),
        note: note.trim(),
      });
      setAppName(""); setDomain(""); setAmount(""); setNote("");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/spend/${id}`);
      await load();
    } catch (e) { /* ignore */ }
  };

  const readSummary = () => {
    if (!summary) return;
    const apps = (summary.by_app || []).slice().sort((a, b) => b.total - a.total);
    const top3 = apps.slice(0, 3);
    const parts = [
      `Sir, your total Emergent spend across all apps is $${summary.total_all_time.toFixed(0)}.`,
      `This month so far: $${summary.total_this_month.toFixed(0)}.`,
      `Last thirty days: $${summary.total_last_30_days.toFixed(0)}.`,
    ];
    if (top3.length) {
      parts.push("Top spend by app:");
      top3.forEach((a, i) => {
        parts.push(`${i+1}. ${a.app_name} at $${a.total.toFixed(0)}.`);
      });
    }
    speak(parts.join(" "));
  };

  const byApp = (summary?.by_app || []).slice().sort((a, b) => b.total - a.total);
  const maxTotal = byApp.length ? Math.max(...byApp.map(a => a.total)) : 1;

  return (
    <div className="min-h-screen text-[var(--rc-text)] relative" data-testid="spend-page">
      <ThemedBackground background="neural_brain" lite={true} />
      <div className="relative z-10 p-4 space-y-4 max-w-6xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <button
              onClick={() => { window.location.href = "/"; }}
              className="rc-panel px-3 py-1.5 text-[10px] rc-glow-text"
              data-testid="spend-back-btn"
            >← BACK</button>
            <h1 className="text-2xl font-bold rc-glow-text">SPEND TRACKER</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={readSummary} disabled={!summary}
                    className="rc-panel px-3 py-1.5 text-[11px] rc-glow-text disabled:opacity-50"
                    data-testid="spend-read-summary-btn">
              🔊 READ SUMMARY
            </button>
            <button onClick={load} disabled={loading}
                    className="rc-panel px-3 py-1.5 text-[11px] rc-glow-text disabled:opacity-50"
                    data-testid="spend-refresh-btn">
              {loading ? "..." : "↻ REFRESH"}
            </button>
          </div>
        </div>

        {err && (
          <div className="rc-panel p-3 text-[11px]" style={{ borderColor: "#f87171", color: "#f87171" }}>
            {err}
          </div>
        )}

        {/* Summary tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rc-panel p-4" data-testid="spend-total-all">
            <div className="rc-label text-[10px] rc-glow-text">// ALL-TIME TOTAL</div>
            <div className="text-3xl font-bold rc-glow-text mt-2">
              ${summary ? summary.total_all_time.toFixed(2) : "—"}
            </div>
          </div>
          <div className="rc-panel p-4" data-testid="spend-total-month">
            <div className="rc-label text-[10px] rc-glow-text">// THIS MONTH</div>
            <div className="text-3xl font-bold mt-2" style={{ color: "#86efac" }}>
              ${summary ? summary.total_this_month.toFixed(2) : "—"}
            </div>
          </div>
          <div className="rc-panel p-4" data-testid="spend-total-30d">
            <div className="rc-label text-[10px] rc-glow-text">// LAST 30 DAYS</div>
            <div className="text-3xl font-bold mt-2" style={{ color: "#fcd34d" }}>
              ${summary ? summary.total_last_30_days.toFixed(2) : "—"}
            </div>
          </div>
        </div>

        {/* By-app bars */}
        <div className="rc-panel p-4" data-testid="spend-by-app">
          <div className="rc-label text-[10px] rc-glow-text mb-3">// BY APP</div>
          {byApp.length === 0 && (
            <div className="text-[11px] opacity-60">No entries yet. Add one below or run /api/spend/seed.</div>
          )}
          <div className="space-y-2">
            {byApp.map((a) => {
              const pct = (a.total / maxTotal) * 100;
              return (
                <div key={a.app_name} className="text-[11px]" data-testid={`spend-row-${a.app_name}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="rc-glow-text font-bold">{a.app_name}</span>
                    <span>${a.total.toFixed(2)} <span className="opacity-50">· month ${a.month.toFixed(2)}</span></span>
                  </div>
                  <div className="h-2 bg-[var(--rc-gold-dim)]/30">
                    <div className="h-full" style={{ width: `${pct}%`, background: "var(--rc-gold)" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Add entry form */}
        <div className="rc-panel p-4" data-testid="spend-add-form">
          <div className="rc-label text-[10px] rc-glow-text mb-3">// LOG NEW SPEND</div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <input value={appName} onChange={e => setAppName(e.target.value)} placeholder="App name *"
                   className="rc-panel px-2 py-1.5 text-[11px] bg-transparent"
                   data-testid="spend-input-app"/>
            <input value={domain} onChange={e => setDomain(e.target.value)} placeholder="domain.uk"
                   className="rc-panel px-2 py-1.5 text-[11px] bg-transparent"
                   data-testid="spend-input-domain"/>
            <input value={amount} onChange={e => setAmount(e.target.value)} type="number" step="0.01" placeholder="$ amount *"
                   className="rc-panel px-2 py-1.5 text-[11px] bg-transparent"
                   data-testid="spend-input-amount"/>
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="note"
                   className="rc-panel px-2 py-1.5 text-[11px] bg-transparent"
                   data-testid="spend-input-note"/>
            <button onClick={addEntry} disabled={saving || !appName.trim() || !amount}
                    className="rc-panel px-3 py-1.5 text-[11px] rc-glow-text disabled:opacity-50"
                    data-testid="spend-add-btn">
              {saving ? "SAVING..." : "+ LOG SPEND"}
            </button>
          </div>
        </div>

        {/* Entries table */}
        <div className="rc-panel p-4" data-testid="spend-entries">
          <div className="rc-label text-[10px] rc-glow-text mb-3">// ENTRIES ({entries.length})</div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-[var(--rc-text-dim)] text-[10px]">
                  <th className="text-left py-1">Date</th>
                  <th className="text-left">App</th>
                  <th className="text-left">Domain</th>
                  <th className="text-right">Amount</th>
                  <th className="text-left">Note</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className="border-t border-[var(--rc-gold-dim)]/30">
                    <td className="py-1.5 opacity-70">{e.date}</td>
                    <td className="rc-glow-text font-bold">{e.app_name}</td>
                    <td className="opacity-70">{e.domain}</td>
                    <td className="text-right">${(e.amount_usd ?? 0).toFixed(2)}</td>
                    <td className="opacity-60 truncate max-w-[200px]">{e.note}</td>
                    <td className="text-right">
                      <button onClick={() => removeEntry(e.id)}
                              className="text-[10px] opacity-50 hover:opacity-100 hover:text-[#f87171]"
                              data-testid={`spend-delete-${e.id}`}>×</button>
                    </td>
                  </tr>
                ))}
                {entries.length === 0 && (
                  <tr><td colSpan="6" className="py-4 text-center opacity-50">No entries yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
